/*
 * Free shipping progress bar + "undo remove" toast for the cart drawer.
 *
 * The bar markup is rendered server-side by snippets/free-shipping-bar.liquid
 * and re-rendered on every cart update (it lives inside <cart-drawer-items>).
 * This file only animates the fill width and wires up the undo toast.
 */

// Persist the last rendered percentage across re-renders so the bar animates
// from its previous value to the new target instead of snapping.
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    const fill = this.querySelector('.free-shipping-bar__fill');
    if (!fill) return;

    const target = parseFloat(this.dataset.percent) || 0;
    const previous = typeof window.freeShippingBarPercent === 'number' ? window.freeShippingBarPercent : 0;

    fill.style.width = `${previous}%`;
    // Force a reflow so the browser registers the starting width before the
    // transition to the target width runs.
    void fill.offsetWidth;
    requestAnimationFrame(() => {
      fill.style.width = `${target}%`;
    });

    window.freeShippingBarPercent = target;
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}

// --- Undo remove --------------------------------------------------------------
(function initCartUndo() {
  if (window.cartUndoInitialized) return;
  window.cartUndoInitialized = true;

  const strings = () =>
    window.cartUndoStrings || { undo: 'Undo', removed: '[title] removed', error: "Couldn't restore" };

  let pending = null;
  let pendingTimer = null;
  let toast = null;
  let toastTimer = null;

  // Capture the line details while the row is still in the DOM (removal is async).
  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('.cart-remove-button');
    if (!button) return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const row = button.closest('.cart-item');
    const input = row && row.querySelector('.quantity__input');
    const nameEl = row && row.querySelector('.cart-item__name');

    pending = {
      variantId,
      quantity: input ? parseInt(input.value, 10) || 1 : 1,
      title: nameEl ? nameEl.textContent.trim() : '',
    };

    clearTimeout(pendingTimer);
    pendingTimer = setTimeout(() => {
      pending = null;
    }, 4000);
  });

  function ensureToast() {
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'cart-undo-toast';
    toast.setAttribute('role', 'status');

    const text = document.createElement('span');
    text.className = 'cart-undo-toast__text';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cart-undo-toast__button';
    btn.textContent = strings().undo;
    btn.addEventListener('click', onUndoClick);

    toast.append(text, btn);
    document.body.appendChild(toast);
    return toast;
  }

  function showToast(item) {
    ensureToast();
    toast.dataset.variantId = item.variantId;
    toast.dataset.quantity = String(item.quantity);
    toast.querySelector('.cart-undo-toast__text').textContent = strings().removed.replace('[title]', item.title || '');
    toast.querySelector('.cart-undo-toast__button').textContent = strings().undo;
    toast.querySelector('.cart-undo-toast__button').disabled = false;
    toast.classList.add('cart-undo-toast--visible');

    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 6000);
  }

  function hideToast() {
    if (toast) toast.classList.remove('cart-undo-toast--visible');
  }

  function showToastError() {
    if (!toast) return;
    toast.querySelector('.cart-undo-toast__text').textContent = strings().error;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, 4000);
  }

  function onUndoClick() {
    const variantId = toast.dataset.variantId;
    const quantity = parseInt(toast.dataset.quantity, 10) || 1;
    if (!variantId || typeof routes === 'undefined') return;

    toast.querySelector('.cart-undo-toast__button').disabled = true;

    const cartDrawer = document.querySelector('cart-drawer');
    const sections =
      cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];

    const body = JSON.stringify({
      id: variantId,
      quantity,
      sections,
      sections_url: window.location.pathname,
    });

    const config = typeof fetchConfig === 'function' ? fetchConfig() : {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    };

    fetch(`${routes.cart_add_url}`, { ...config, body })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          showToastError();
          return;
        }
        if (cartDrawer) {
          cartDrawer.classList.remove('is-empty');
          if (typeof cartDrawer.renderContents === 'function') {
            cartDrawer.renderContents(response);
          }
        }
        hideToast();
      })
      .catch(() => showToastError());
  }

  // Show the toast only once the removal is confirmed by a cart update.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (!pending) return;
      if (event && event.source && event.source !== 'cart-items') return;

      const item = pending;
      pending = null;
      clearTimeout(pendingTimer);

      // If the cart still contains the variant, it was a quantity change, not a removal.
      const data = event && event.cartData;
      if (data && Array.isArray(data.items)) {
        const stillThere = data.items.some(
          (line) => String(line.variant_id) === String(item.variantId) || String(line.id) === String(item.variantId)
        );
        if (stillThere) return;
      }

      showToast(item);
    });
  }
})();
